/**
 * Marquee Class
 *
 * A JavaScript class that creates infinite scrolling marquee effects for content.
 * Automatically clones content to ensure seamless infinite scrolling and handles
 * responsive behavior with ResizeObserver.
 *
 * class Marquee
 * description Creates and manages infinite scrolling marquee animations
 *
 * example
 * // HTML structure required:
 * <div class="flex gap-[--marquee-gap] [--marquee-gap:20px] md:[--marquee-gap:40px] overflow-hidden" data-marquee>
 *   <ul class="flex gap-5" animate-marquee gap-[--marquee-gap] data-marquee-items>
 *     <li class="shrink-0">Item 1</li>
 *     <li class="shrink-0">Item 2</li>
 *     <li class="shrink-0">Item 3</li>
 *   </ul>
 * </div>
 *
 * // JavaScript usage:
 * import initMarquees from './marquee.js';
 * const marquees = initMarquees();
 *
 * example
 * // Manual instantiation:
 * const marqueeElement = document.querySelector('[data-marquee]');
 * const marquee = new Marquee(marqueeElement);
 */

class Marquee {
    constructor(element) {
        this.marquee = element
        this.marqueeItems = [] // this will be populated in getMarqueeItems()
        this.marqueeWidth = null // this will be populated in calculateMarqueeWidth()
        this.marqueeContainer = this.marquee.parentElement
        this.marqueeContainerWidth = this.marqueeContainer.offsetWidth
        this.marqueeGap = this.marquee.getAttribute('data-marquee-gap') ?? 20
        this.originalItem = null // Store reference to original item for cloning

        this._init()
    }

    _init() {
        this.calculateMarqueeWidth()
        this.storeOriginalItem()
        this.cloneContent() // we need at least one duplicate regardless of screen/marquee width
        this.fillMarquee()
        this.setupResizeObserver() // Monitor parent width changes
    }

    storeOriginalItem() {
        // Store reference to the original item before any cloning
        const items = this.marquee.querySelectorAll('[data-marquee-items]')
        this.originalItem = items[0]
    }

    fillMarquee() {
        while (this.marqueeWidth < this.marqueeContainerWidth * 2) {
            this.cloneContent()
            this.calculateMarqueeWidth()
        }
    }

    calculateMarqueeWidth() {
        this.getMarqueeItems()

        // Get the computed gap value from CSS
        const computedStyle = window.getComputedStyle(this.marquee)
        const gapValue = this.marqueeGap

        let width = 0
        this.marqueeItems.forEach((item) => {
            width += item.offsetWidth

            // Add gaps between items within each ul
            const itemCount = item.children.length
            const gapsInThisUl = Math.max(0, itemCount - 1)

            width += gapsInThisUl * gapValue
        })

        this.marqueeWidth = width
        console.log('this.marqueeWidth (with gaps)', this.marqueeWidth)
    }

    getMarqueeItems() {
        const items = this.marquee.querySelectorAll('[data-marquee-items]')
        this.marqueeItems = Array.from(items)
    }

    cloneContent() {
        if (this.originalItem) {
            this.marquee.appendChild(this.originalItem.cloneNode(true))
        }
    }

    resetMarquee() {
        // Remove all cloned items, keeping only the original
        this.getMarqueeItems()
        if (this.marqueeItems.length > 1) {
            // Remove all but the first item (original)
            for (let i = this.marqueeItems.length - 1; i > 0; i--) {
                this.marqueeItems[i].remove()
            }
        }

        // Recalculate and refill
        this.calculateMarqueeWidth()
        this.fillMarquee()
    }

    setupResizeObserver() {
        // Use ResizeObserver to monitor parent width changes
        if (window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver(() => {
                const newContainerWidth = this.marqueeContainer.offsetWidth

                // Only add more content if parent got wider
                if (newContainerWidth > this.marqueeContainerWidth) {
                    this.marqueeContainerWidth = newContainerWidth

                    // Pause all animations
                    this.marqueeItems.forEach((group) => {
                        group.style.animationPlayState = 'paused'
                    })

                    // Reset and refill marquee
                    this.resetMarquee()

                    // Resume animations with a slight delay to ensure DOM updates
                    requestAnimationFrame(() => {
                        this.getMarqueeItems()
                        this.marqueeItems.forEach((group) => {
                            group.style.animationPlayState = 'running'
                        })
                    })
                }
            })

            this.resizeObserver.observe(this.marqueeContainer)
        }
    }

    destroy() {
        // Clean up the ResizeObserver
        if (this.resizeObserver) {
            this.resizeObserver.disconnect()
        }
    }

    _getAnimateClass() {
        console.log(this.marqueeItems[0].classList)
    }
}

// Initialize all marquees on the page
export default function initMarquees() {
    const marqueeElements = document.querySelectorAll('[data-marquee]')
    const marqueeInstances = []

    marqueeElements.forEach((element) => {
        const marqueeInstance = new Marquee(element)
        marqueeInstances.push(marqueeInstance)
    })

    return marqueeInstances
}

initMarquees()
